export interface SessionState {
  sessionId: string | null;
  matterportSid: string | null;
  startTime: number;
  currentZone: string | null;
  zoneEnterTime: number;
  zoneTimes: Record<string, number>;
  zonesVisited: Set<string>;
  usedVr: boolean;
  vrStartTime: number | null;
  vrTotalTime: number;
  totalNodes: number;
}

export const createSession = (): SessionState => ({
  sessionId: null,
  matterportSid: null,
  startTime: 0,
  currentZone: null,
  zoneEnterTime: 0,
  zoneTimes: {},
  zonesVisited: new Set(),
  usedVr: false,
  vrStartTime: null,
  vrTotalTime: 0,
  totalNodes: 0,
});

export const recordZoneTransition = (
  state: SessionState,
  zoneName: string,
  now: number,
): SessionState => {
  const next: SessionState = {
    ...state,
    zoneTimes: { ...state.zoneTimes },
    zonesVisited: new Set(state.zonesVisited),
  };
  if (next.currentZone && next.currentZone !== zoneName) {
    next.zoneTimes[next.currentZone] =
      (next.zoneTimes[next.currentZone] || 0) + (now - next.zoneEnterTime);
  }
  if (next.currentZone !== zoneName) {
    next.zonesVisited.add(zoneName);
    next.currentZone = zoneName;
    next.zoneEnterTime = now;
  }
  return next;
};

export const zoneTimesMsToSeconds = (
  zoneTimes: Record<string, number>,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(zoneTimes).map(([k, v]) => [k, Math.round(v / 1000)]),
  );

export const computeMostViewedZone = (
  zoneTimesSec: Record<string, number>,
): { zone: string; seconds: number } | null => {
  const ranked = Object.entries(zoneTimesSec).sort((a, b) => b[1] - a[1])[0];
  return ranked ? { zone: ranked[0], seconds: ranked[1] } : null;
};

export interface StartPayloadInput {
  matterport: { matterport_id: string; slug?: string };
  projectId: string;
  agencyId: string;
  agencySlug: string;
  projectSlug: string;
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  userEmail: string;
  shareSrc: string | null;
  shareCode: string | null;
  // Mongo _id of the share-link click that started this visit. Bundles all
  // sessions from one click under one id so Share Analytics can collapse
  // "7 sessions in 1 click" → 1 Tour. Null on platform/organic sessions.
  clickId: string | null;
  referrer: string;
  isMobile: boolean;
  // Website-embed placement key, set only when this tour is playing inside an
  // embed. The backend reads it off the session for three things at once: the
  // lead's `source = embed`, which site it came from, and which OTP template
  // the SMS uses. Without it an embed lead is indistinguishable from a direct
  // visit and gets the generic "login to the portal" text.
  embedKey?: string | null;
  /**
   * The visitor's own campaign tags, forwarded from the PARENT page by the embed snippet.
   * An iframe cannot read its parent's URL, so a paid visitor's utm_* would be invisible to
   * us without that hop — and a CRM that reports Source from utm_source would file every
   * walkthrough lead as organic.
   */
  utm?: Record<string, string> | null;
}

export const buildStartPayload = (o: StartPayloadInput) => {
  // A share-link session belongs to the VIEWER (a buyer) and is attributed to
  // the SHARER via referrer_share_code — NOT to whoever happens to be logged in
  // while opening the link. So when a share code is present, the agency-side
  // role IDs (aba/asa/agent/customer) and the viewer email are left blank;
  // attribution flows purely through referrer_share_code.
  const isShared = !!o.shareCode;
  return {
    matterport_id: o.matterport.matterport_id,
    project_id: o.projectId,
    agency_id: o.agencyId,
    experience_type: "mp" as const,
    agency_slug: o.agencySlug,
    project_slug: o.projectSlug,
    experience_slug: o.matterport.slug || o.matterport.matterport_id,
    aba_id: isShared || !o.clientManagerId ? [] : [o.clientManagerId],
    asa_id: isShared || !o.clientAdminId ? [] : [o.clientAdminId],
    agent_id: isShared || !o.agentId ? [] : [o.agentId],
    customer_id: isShared || !o.customerId ? [] : [o.customerId],
    user_id: isShared ? "" : o.userEmail || "",
    device: o.isMobile ? ("mobile" as const) : ("desktop" as const),
    referrer_website: o.referrer,
    // Channel name from the share-link `?src=<channel>` URL param
    // (whatsapp / copy / qr / etc). Null for organic / non-shared sessions.
    src: o.shareSrc,
    // Share-link attribution code from `?ref=<code>`. Null on the platform
    // (no ref param); set when viewed via a shared link. Backend stamps this
    // on the vr_session (write-once) so Share Analytics can attribute it.
    referrer_share_code: o.shareCode,
    // Click that started this visit (from the /click response). Bundles
    // sessions per click for the Tours metric. Null on platform/organic.
    click_id: o.clickId,
    vr_type: "self exploratory" as const,
    // Omitted entirely rather than sent as null, so every non-embed session
    // keeps exactly the payload it had before this field existed.
    ...(o.embedKey ? { embed_key: o.embedKey } : {}),
    ...(o.utm || {}),
  };
};

export interface CompletePayloadInput {
  sessionId: string;
  matterport: { matterport_id: string } | null | undefined;
  agencyId: string;
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  // Share-link code from `?ref=`. When present this is a share session whose
  // attribution flows via referrer_share_code only — so the agency-side role
  // IDs are blanked on COMPLETE too, mirroring buildStartPayload. Without this
  // the complete call would overwrite the start's null role IDs with whoever
  // is logged in while the share link is open. null/omitted on platform/organic.
  shareCode?: string | null;
  state: SessionState;
  endTime: number;
}

export const buildCompletePayload = (o: CompletePayloadInput) => {
  let state = o.state;
  // Finalize current zone time
  if (state.currentZone) {
    state = {
      ...state,
      zoneTimes: {
        ...state.zoneTimes,
        [state.currentZone]:
          (state.zoneTimes[state.currentZone] || 0) +
          (o.endTime - state.zoneEnterTime),
      },
    };
  }
  // Finalize VR time if a VR session is still open
  const vrTotalMs = state.vrStartTime
    ? state.vrTotalTime + (o.endTime - state.vrStartTime)
    : state.vrTotalTime;
  const zoneSecs = zoneTimesMsToSeconds(state.zoneTimes);
  const ranked = computeMostViewedZone(zoneSecs);
  const totalDuration = Math.round((o.endTime - state.startTime) / 1000);
  // Share sessions are attributed via referrer_share_code only — blank the
  // agency-side role IDs so the complete call doesn't re-credit the session
  // to whoever is logged in while the share link is open (mirrors buildStartPayload).
  const isShared = !!o.shareCode;
  return {
    session_id: o.sessionId,
    matterport_id: o.matterport?.matterport_id,
    agency_id: o.agencyId,
    aba_id: isShared || !o.clientManagerId ? [] : [o.clientManagerId],
    asa_id: isShared || !o.clientAdminId ? [] : [o.clientAdminId],
    agent_id: isShared || !o.agentId ? [] : [o.agentId],
    customer_id: isShared || !o.customerId ? [] : [o.customerId],
    total_duration: totalDuration,
    zones_visited: Array.from(state.zonesVisited),
    zone_times: zoneSecs,
    most_viewed_zone: ranked?.zone || "",
    most_viewed_time: ranked?.seconds || 0,
    total_nodes: state.totalNodes,
    used_vr: state.usedVr,
    vr_time: Math.round(vrTotalMs / 1000),
    type: "mp" as const,
    vr_type: "self exploratory" as const,
  };
};
